import { TypesenseData } from '@/lib/typesenseFunction'
import { ArthoPage, CompactProfileList, Individualprofile, KeyProfileList, Legacyprofile, Organizationprofile, ProfileList, Publicprofile, RegularProfileList, University } from "@/payload-types";
import { IGraphQLResponse } from '@/types/graphql';
import { getAllDocsFromCollection, getCollectionSpecificFields, getCollectionSpecificFieldsByVersion } from './fetchDataFromPayload';
import { arthoPageDataFields, individualDataFields, metaDataFields } from './queryFields/individualQueryFields';
import { CANARY_MISSION } from '@/enum/enum';
import { fetchJsonFromUrl } from './arthoContentUrl';
import { Campaign as CampaignObject } from '@/payload-types'
import { ArthoPageData } from '@/lib/metadataGenerators/types'

function arrayToString(
    arrayToReduce: (string | null | undefined)[] | undefined | (string | Organizationprofile)[] | (string | University)[] | object[]
) {
    return arrayToReduce && arrayToReduce.reduce((result, item, index) => {
        const itemString =
            typeof item === 'string' ? item : item && 'acronym' in item ? (item?.acronym || '') : item && 'name' in item ? (item?.name || '') : '';
        return result + itemString + (index !== arrayToReduce.length - 1 ? ', ' : '');
    }, '');
}

export function createProfileDetailsForTS(typesenseObj: TypesenseData) {
    const arrayOfData: [{ label: string | null, value: string | null }] = [{ label: null, value: null }]
    if (typeof typesenseObj === 'object' && typesenseObj) {
        const profileStatus = typesenseObj.status ? arrayToString(typesenseObj.status) : null
        profileStatus && arrayOfData.push({ label: 'Status', value: typeof profileStatus === 'string' ? profileStatus : '' })

        const state = typesenseObj.state
        state && arrayOfData.push({ label: 'State', value: state })

        const profileOrganizations = typesenseObj.organizations
            ? arrayToString(typesenseObj.organizations)
            : ''
        profileOrganizations && arrayOfData.push({ label: 'Org', value: typeof profileOrganizations === 'string' ? profileOrganizations : '' })

        const profileUniversities = typesenseObj.universities
            ? arrayToString(typesenseObj.universities)
            : ''
        profileUniversities && arrayOfData.push({ label: 'University', value: typeof profileUniversities === 'string' ? profileUniversities : '' })
    }
    return arrayOfData;
}

export function createProfileDetails(profileData: (string | null) | Legacyprofile | Publicprofile) {
    const arrayOfData: [{ label: string | null, value: string | null }] = [{ label: null, value: null }]
    if (typeof profileData === 'object' && profileData) {
        if ('profileEntity' in profileData && typeof profileData.profileEntity === 'object') {
            const profileStatus = profileData?.profileEntity.status
            profileStatus && arrayOfData.push({ label: 'Status', value: profileStatus })

            let state = null
            if (profileData.profileEntity.country === "usa" && profileData.profileEntity.usaState) {
                state = profileData.profileEntity?.usaState
            }
            else if (profileData.profileEntity.country === "canada" && profileData.profileEntity.canadaState) {
                state = profileData.profileEntity?.canadaState
            }
            const stateWithSpace = state ? state.replace(/([a-z])([A-Z])/g, '$1 $2') : ''
            state && arrayOfData.push({ label: 'State', value: stateWithSpace })

            const organizationArray = profileData?.profileEntity.organizations?.map((org) => {
                return typeof org === 'object' ? org?.name : ''
            })
            const profileOrganizations = profileData?.profileEntity.organizations
                ? arrayToString(
                    organizationArray
                )
                : ''
            profileOrganizations && arrayOfData.push({ label: 'Org', value: typeof profileOrganizations === 'string' ? profileOrganizations : '' })

            const profileUniversities = profileData?.profileEntity.universities
                ? arrayToString(profileData?.profileEntity.universities)
                : ''
            profileUniversities && arrayOfData.push({ label: 'University', value: typeof profileUniversities === 'string' ? profileUniversities : '' })
        }
        if (!('profileEntity' in profileData) && profileData) {
            const profileStatus = profileData.professions ? arrayToString(profileData?.professions?.map((item) => item.profession)) : 'Unknown'

            profileStatus && arrayOfData.push({ label: 'Status', value: typeof profileStatus === 'string' ? profileStatus : '' })
            const state = profileData.state

            state && arrayOfData.push({ label: 'State', value: state })

            const profileOrganizations = profileData?.organizations
                ? arrayToString(
                    profileData.organizations.map((item) => (item.organization ? item.organization : '')),
                )
                : ''
            profileOrganizations && arrayOfData.push({ label: 'Org', value: typeof profileOrganizations === 'string' ? profileOrganizations : '' })

            const profileUniversities = profileData?.universities
                ? arrayToString(profileData.universities.map((item) => (item.name ? item.name : '')))
                : ''
            profileUniversities && arrayOfData.push({ label: 'University', value: typeof profileUniversities === 'string' ? profileUniversities : '' })
        }
    }
    return arrayOfData;
}

export function SortProfileListByName(profileListData: ProfileList | null) {
    if (profileListData?.profileList) {
        const sortedList = [...profileListData.profileList];
        sortedList.sort((a, b) => {
            const aName =
                typeof a.profile?.value === 'object'
                    ? a.profile.relationTo === 'legacyprofiles'
                        ? a.profile.value.fullName
                        : typeof a.profile?.value.profileEntity === 'object'
                            ? a.profile?.value.profileEntity.fullName
                            : ''
                    : ''

            const bName =
                typeof b.profile?.value === 'object'
                    ? b.profile.relationTo === 'legacyprofiles'
                        ? b.profile.value.fullName
                        : typeof b.profile?.value.profileEntity === 'object'
                            ? b.profile?.value.profileEntity.fullName
                            : ''
                    : ''

            return (aName ?? '').localeCompare(bName ?? '', undefined, { sensitivity: 'base' })
        })
        return sortedList;
    }
    return profileListData?.profileList;
}

export function SortProfileListAlphabetically(profileListData: RegularProfileList | CompactProfileList | KeyProfileList | null) {
    if (profileListData?.hasManyProfileList) {
        const sortedList = profileListData.hasManyProfileList;
        sortedList.sort((a, b) => {
            const aName =
                typeof a.value === 'object'
                    ? a.relationTo === 'legacyprofiles'
                        ? a.value.fullName
                        : typeof a.value.profileEntity === 'object'
                            ? a.value.profileEntity.fullName
                            : ''
                    : ''

            const bName =
                typeof b.value === 'object'
                    ? b.relationTo === 'legacyprofiles'
                        ? b.value.fullName
                        : typeof b.value.profileEntity === 'object'
                            ? b.value.profileEntity.fullName
                            : ''
                    : ''

            return (aName ?? '').localeCompare(bName ?? '', undefined, { sensitivity: 'base' })
        })
        return sortedList;
    }
    return profileListData?.hasManyProfileList;
}

export function getFullNameCSS(fullNameLength: number) {
    switch (true) {
        case fullNameLength < 15:
            return 'text-lg'
        case fullNameLength >= 15 && fullNameLength < 18:
            return 'text-base'
        case fullNameLength >= 18 && fullNameLength < 20:
            return 'text-sm'
        case fullNameLength >= 20:
            return 'text-xs'
        default:
            return 'text-lg'
    }
}


export async function getStaticProfilesPage(collectionName: string, staticParamsFields: string, path: string) {
    const isStaticBuildMigration = process.env.STATIC_BUILD_MIGRATION === 'true'
    const profileStatus = isStaticBuildMigration ? 'draft' : 'published'
    const data: IGraphQLResponse<Publicprofile, 'Publicprofiles'> =
        (await getCollectionSpecificFields(collectionName, staticParamsFields, `_status: { equals: ${profileStatus} }`)) as IGraphQLResponse<
            Publicprofile,
            'Publicprofiles'
        >
    const allPublicprofileDocs = isStaticBuildMigration
        ? data.Publicprofiles.docs.filter(doc => typeof doc.profileEntity === 'object' && doc.profileEntity?.isFromArtho === true)
        : data.Publicprofiles.docs
    if (allPublicprofileDocs.length === 0) {
        return [{ slug: 'dummy' }]
    } else {
        let filteredDocs
        switch (path) {
            case 'individual':
                filteredDocs = allPublicprofileDocs.filter(
                    (doc) => typeof doc.profileEntity === 'object' && doc.profileEntity?.status !== "Professor",
                )
                break;
            case 'professor':
                filteredDocs = allPublicprofileDocs.filter(
                    (doc) => typeof doc.profileEntity === 'object' && doc.profileEntity?.status === "Professor",
                )
                break;

        }
        return filteredDocs?.map((doc) => ({ slug: doc.slug }))
    }
}

export async function getStaticArthoIndividualSlugs(path: 'individual' | 'professor') {
    const data = (await getCollectionSpecificFields(
        'ArthoPages',
        `slug
        type`,
        `status: { equals: published }`,
    )) as IGraphQLResponse<Pick<ArthoPage, 'slug'> & { type?: string }, 'ArthoPages'>
    const allArthoDocs = data.ArthoPages.docs ?? []
    if (data.ArthoPages.totalDocs === 0) {
        return [{ slug: 'dummy' }]
    }
    let filteredDocs
    switch (path) {
        case 'individual':
            filteredDocs = allArthoDocs.filter((doc) => doc.type === 'individual')
            break
        case 'professor':
            filteredDocs = allArthoDocs.filter((doc) => doc.type === 'professor')
            break
    }
    return (filteredDocs ?? [])
        .filter((d): d is { slug: string } => typeof d.slug === 'string' && d.slug.length > 0)
        .map((d) => ({ slug: d.slug }))
}

export async function getMergedStaticProfileParams(
    collectionName: string,
    profileStaticFields: string,
    path: 'individual' | 'professor',
) {
    const [arthoSlugs , profileSlugs ] = await Promise.all([
        getStaticArthoIndividualSlugs(path),
        getStaticProfilesPage(collectionName, profileStaticFields, path),
        
    ])

    const fromProfiles = (profileSlugs ?? []).filter((s) => s.slug && s.slug !== 'dummy')
    const seen = new Set<string>()
    const merged: { slug: string }[] = []
    for (const row of [...arthoSlugs, ...fromProfiles]) {
        const slug = row.slug
        if (!slug || seen.has(slug)) continue
        seen.add(slug)
        merged.push({ slug })
    }

    return merged.length > 0 ? merged : [{ slug: 'dummy' }]
}

export async function getMergedStaticArthoBlogSlugs() {
    const [arthoData, blogData] = await Promise.all([
      getCollectionSpecificFields(
        'ArthoPages',
        `slug
         type`,
        `status: { equals: published },
         type: { equals: blog }`,
      ) as Promise<IGraphQLResponse<ArthoPage, 'ArthoPages'>>,
  
      getAllDocsFromCollection('Blogs', 'id, slug'),
    ])
  
    const arthoDocs = arthoData?.ArthoPages?.docs ?? []
    const blogDocs = blogData?.docs ?? []

    const allRows = [...arthoDocs, ...blogDocs]

    const seen = new Set<string>()
    const merged: { slug: string }[] = []
  
    for (const row of allRows) {
      const slug = row?.slug
  
      if (!slug || slug === 'dummy' || seen.has(slug)) continue
  
      seen.add(slug)
      merged.push({ slug })
    }
  
    return merged.length > 0 ? merged : [{ slug: 'dummy' }]
  }

  export async function getMergedStaticCampaignSlugs() {
    const [campaignData, arthoData] = await Promise.all([
      getCollectionSpecificFields(
        'Campaigns',
        'slug',
        `_status: { equals: published }`,
      ) as Promise<IGraphQLResponse<CampaignObject, 'Campaigns'>>,
  
      getCollectionSpecificFields(
        'ArthoPages',
        'slug, type',
        `status: { equals: published },
         type: { equals: campaign }`,
      ) as Promise<IGraphQLResponse<ArthoPage, 'ArthoPages'>>,
    ])
  
    const campaignDocs = campaignData?.Campaigns?.docs ?? []
    const arthoDocs = arthoData?.ArthoPages?.docs ?? []
  
    const seen = new Set<string>()
  
    const merged: { slug: string }[] = []
  
    for (const row of [...arthoDocs, ...campaignDocs]) {
      const slug = row?.slug
  
      if (!slug || slug === 'dummy' || seen.has(slug)) continue
  
      seen.add(slug)
  
      merged.push({ slug })
    }
  
    return merged.length > 0 ? merged : [{ slug: 'dummy' }]
  }

type SitemapSlug = { slug?: string | null, updatedAt?: string }

const mergeUniqueSlugs = <T extends SitemapSlug>(
  rows: Array<Array<T | null | undefined>>,
): Array<T & { slug: string, updatedAt: string }> => {
  const seen = new Set<string>()
  const merged: Array<T & { slug: string, updatedAt: string }> = []

  for (const group of rows) {
    for (const row of group) {
      const slug = row?.slug
      if (!slug || slug === 'dummy' || seen.has(slug)) continue
      seen.add(slug)
      merged.push({ ...row, slug, updatedAt: row.updatedAt || '' })
    }
  }

  return merged
}

export type SitemapProfileSlug = {
  slug: string
  updatedAt: string
  status?: Individualprofile['status']
  region?: string | null
  specialities: string[]
}

type SitemapProfileCategory = Pick<Individualprofile, 'status' | 'region' | 'speciality'>

const getSitemapProfileCategory = (
  profile: SitemapProfileCategory | null | undefined,
): Omit<SitemapProfileSlug, 'slug' | 'updatedAt'> => ({
  status: profile?.status,
  region: profile?.region,
  specialities: (profile?.speciality ?? [])
    .map((speciality) => typeof speciality === 'object' ? speciality.name : undefined)
    .filter((name): name is string => Boolean(name)),
})

/** Sitemap-only: published Artho + Eco profile slugs */
export async function getSitemapProfileSlugs(path: 'individual' | 'professor'): Promise<SitemapProfileSlug[]> {
  const [arthoData, profileData] = await Promise.all([
    getCollectionSpecificFields(
      'ArthoPages',
      `slug
       type
       ecoPublicProfileId
       updatedAt`,
      `status: { equals: published },
       type: { equals: ${path} }`,
    ) as Promise<IGraphQLResponse<Pick<ArthoPage, 'slug' | 'updatedAt' | 'ecoPublicProfileId'>, 'ArthoPages'>>,
    getCollectionSpecificFields(
      'Publicprofiles',
      `id
       slug
       updatedAt
       profileEntity {
         status
         region
         speciality {
           name
         }
       }`,
      `_status: { equals: published }`,
    ) as Promise<IGraphQLResponse<Publicprofile, 'Publicprofiles'>>,
  ])

  const categoriesByPublicProfileId = new Map(
    (profileData.Publicprofiles.docs ?? []).flatMap((profile) => {
      return typeof profile.profileEntity === 'object' && profile.profileEntity
        ? [[profile.id, getSitemapProfileCategory(profile.profileEntity)] as const]
        : []
    }),
  )

  const arthoSlugs = (arthoData.ArthoPages.docs ?? [])
    .filter((d): d is { slug: string, updatedAt: string, ecoPublicProfileId?: string } => typeof d.slug === 'string' && d.slug.length > 0 && typeof d.updatedAt === 'string' && typeof d.ecoPublicProfileId === 'string')
    .map((d) => {
      const category = d.ecoPublicProfileId
        ? categoriesByPublicProfileId.get(d.ecoPublicProfileId)
        : undefined
      return {
        slug: d.slug,
        updatedAt: d.updatedAt,
        ...category,
        specialities: category?.specialities ?? [],
        status: path === 'professor' ? 'Professor' as const : category?.status,
      }
    })

  const profileSlugs = (profileData.Publicprofiles.docs ?? [])
    .filter((doc) => {
      if (typeof doc.profileEntity !== 'object' || !doc.profileEntity) return false
      const isProfessor = doc.profileEntity.status === 'Professor'
      return path === 'professor' ? isProfessor : !isProfessor
    })
    .map((doc) => ({
      slug: doc.slug,
      updatedAt: doc.updatedAt,
      ...getSitemapProfileCategory(
        typeof doc.profileEntity === 'object' ? doc.profileEntity : undefined,
      ),
    }))

  return mergeUniqueSlugs<SitemapProfileSlug>([arthoSlugs, profileSlugs])
}

/** Sitemap-only: published Artho campaign pages + Eco campaigns */
export async function getSitemapCampaignSlugs() {
  const [campaignData, arthoData] = await Promise.all([
    getCollectionSpecificFields(
      'Campaigns',
      'slug, updatedAt',
      `_status: { equals: published }`,
    ) as Promise<IGraphQLResponse<CampaignObject, 'Campaigns'>>,
    getCollectionSpecificFields(
      'ArthoPages',
      'slug, type, updatedAt',
      `status: { equals: published },
       type: { equals: campaign }`,
    ) as Promise<IGraphQLResponse<ArthoPage, 'ArthoPages'>>,
  ])

  return mergeUniqueSlugs<ArthoPage | CampaignObject>([
    arthoData?.ArthoPages?.docs ?? [],
    campaignData?.Campaigns?.docs ?? [],
  ])
}

/** Sitemap-only: published Artho blog pages + Eco blogs */
export async function getSitemapBlogSlugs() {
  const [arthoData, blogData] = await Promise.all([
    getCollectionSpecificFields(
      'ArthoPages',
      `slug
       type
       updatedAt`,
      `status: { equals: published },
       type: { equals: blog }`,
    ) as Promise<IGraphQLResponse<ArthoPage, 'ArthoPages'>>,
    getAllDocsFromCollection('Blogs', 'id, slug, updatedAt'),
  ])

  return mergeUniqueSlugs([
    arthoData?.ArthoPages?.docs ?? [],
    blogData?.docs ?? [],
  ])
}

export async function getArthoHomepagePage() {
    const data = (await getCollectionSpecificFields(
      'ArthoPages',
      arthoPageDataFields,
      `slug: { equals: "homepage" }`,
    )) as IGraphQLResponse<ArthoPage, 'ArthoPages'>
    return data.ArthoPages.docs[0]
  }

export async function getArthoBlogPageBySlug(blogSlug: string) {
    const data = (await getCollectionSpecificFields(
      'ArthoPages',
      arthoPageDataFields,
      `slug: { equals: "${blogSlug}" },
       status: { equals: published },
       type: { equals: blog }`,
    )) as IGraphQLResponse<ArthoPage, 'ArthoPages'>
    return data.ArthoPages.docs[0]
  }

export async function getArthoCampaignPageBySlug(campaignSlug: string) {
    const data = (await getCollectionSpecificFields(
      'ArthoPages',
      arthoPageDataFields,
      `slug: { equals: "${campaignSlug}" },
       status: { equals: published },
       type: { equals: campaign }`,
    )) as IGraphQLResponse<ArthoPage, 'ArthoPages'>
    return data.ArthoPages.docs[0]
  }

export async function getArthoProfilePageBySlug(individualSlug: string) {
    const data = (await getCollectionSpecificFields(
      'ArthoPages',
      arthoPageDataFields,
      `slug: { equals: "${individualSlug}" },
       status: { equals: published },
       OR: [
         { type: { equals: individual } },
         { type: { equals: professor } }
       ]`,
      1,
    )) as IGraphQLResponse<ArthoPage, 'ArthoPages'>
    return data.ArthoPages.docs[0]
  }

export async function getMetaDataOfIndividual(individualSlug: string, collectionName: string, path: string) {
    type ProfileWithSnapshot = Publicprofile & {
        frozenBuildSnapshot?: {
            pageData?: Publicprofile
            metadataData?: Publicprofile
        } | null
    }

    const data: IGraphQLResponse<ProfileWithSnapshot, 'Publicprofiles'> =
        (await getCollectionSpecificFields(
            collectionName,
            `${metaDataFields}
             freezeThisProfile
             frozenBuildSnapshot`,
            `slug: { equals: "${individualSlug}"} `,
        )) as IGraphQLResponse<ProfileWithSnapshot, 'Publicprofiles'>
    const currentProfile = data.Publicprofiles.docs[0]

    if (currentProfile?.freezeThisProfile && !currentProfile.frozenBuildSnapshot?.metadataData) {
        throw new Error(`Frozen profile "${individualSlug}" has no build metadata snapshot. Republish it before building.`)
    }

    const PublicprofileDoc = currentProfile?.freezeThisProfile
        ? currentProfile.frozenBuildSnapshot?.metadataData
        : currentProfile

    if (!PublicprofileDoc) return {}

    const individualTitle = PublicprofileDoc.meta?.title
        ? `${PublicprofileDoc.meta?.title} - ${CANARY_MISSION.NAME}`
        : `${PublicprofileDoc.title} - ${CANARY_MISSION.NAME}`
    const individualPath = `${path}/${individualSlug}`
    const individualDescription = PublicprofileDoc.meta?.description

    const payload_url = process.env.NEXT_PUBLIC_PAYLOAD_URL ? process.env.NEXT_PUBLIC_PAYLOAD_URL : ''
    const individualCoverImage =
        typeof PublicprofileDoc.meta?.image === 'object' &&
            PublicprofileDoc.meta.image?.sizes?.medium?.url
            ? `${payload_url}${PublicprofileDoc.meta.image?.sizes?.medium?.url}`
            : `${payload_url}${typeof PublicprofileDoc.profileImage === 'object' && PublicprofileDoc.profileImage?.sizes?.medium?.url}`

    return { individualTitle, individualPath, individualDescription, individualCoverImage, PublicprofileDoc }
}

export async function getMetaDataOfArthoIndividual(arthoPageSlug: string, collectionName: string, path: string) {
    const data: IGraphQLResponse<ArthoPage, 'ArthoPages'> =
        (await getCollectionSpecificFields(
            collectionName,
            arthoPageDataFields,
            `slug: { equals: "${arthoPageSlug}" },
             status: { equals: published }`,
        )) as IGraphQLResponse<ArthoPage, 'ArthoPages'>
    const arthoDoc = data.ArthoPages.docs[0]
    if (!arthoDoc) return {}

    const ArthoPageDoc = arthoDoc.metaTags

    if (!ArthoPageDoc) return {}

    const arthoPageData = await fetchJsonFromUrl<ArthoPageData>(ArthoPageDoc)

    const arthoPageTitle = `${arthoPageData?.title}`
    const arthoPagePath = `${path}/${arthoPageSlug}`
    const arthoPageDescription = arthoPageData?.description

    return { arthoPageTitle, arthoPagePath, arthoPageDescription, arthoPageData }
}

export const getCasesFromPublicProfile = (individualData: Publicprofile) => {
    return 'profileEntity' in individualData && typeof individualData.profileEntity === 'object'
        ? individualData.profileEntity?.cases?.map((c) =>
            typeof c.case === 'object' ? c.case?.id : '',
        ) || []
        : []
}

type EcoCardProfile = Pick<Publicprofile, 'id' | 'profileImage' | 'profileEntity' | 'title'>

const ecoCardFields = `
  id
  title
  profileImage { sizes { webp { url } medium { url } } }
  profileEntity { fullName status }
`

async function enrichIndividualSeeMoreArtho(individual: Individualprofile): Promise<Individualprofile> {
    if (!individual.seemoreprofiles?.length) return individual
    const ecoIds = [...new Set(individual.seemoreprofiles
        .filter((item) => item.relationTo === 'arthoPages' && typeof item.value === 'object')
        .map((item) => (item.value as ArthoPage).ecoPublicProfileId)
        .filter((id): id is string => Boolean(id)))]
    if (!ecoIds.length) return individual

    const data = await getCollectionSpecificFields(
        'Publicprofiles',
        ecoCardFields,
        `id: { in: [${ecoIds.map((id) => `"${id}"`).join(', ')}] }`,
        ecoIds.length,
    ) as IGraphQLResponse<EcoCardProfile, 'Publicprofiles'>
    const byId = new Map((data.Publicprofiles.docs ?? []).map((doc) => [doc.id, doc]))
    return {
        ...individual,
        seemoreprofiles: individual.seemoreprofiles.map((item) => {
            if (item.relationTo !== 'arthoPages' || typeof item.value !== 'object') return item
            const page = item.value as ArthoPage
            return { ...item, value: { ...page, ecoPublicProfile: page.ecoPublicProfileId ? byId.get(page.ecoPublicProfileId) ?? null : null } }
        }),
    }
}

async function enrichPublicProfileSeeMore(profile: Publicprofile | null | undefined) {
    if (!profile) return null
    if (typeof profile.profileEntity !== 'object' || !profile.profileEntity) return profile
    return { ...profile, profileEntity: await enrichIndividualSeeMoreArtho(profile.profileEntity) }
}

export async function getIndividualData(individualSlug: string, isDraft: boolean, versionId?: string) {

    if (versionId) {
        const data =
            (await getCollectionSpecificFieldsByVersion(
                'versionsPublicprofiles',
                individualDataFields,
                versionId,
                1,
            )) as IGraphQLResponse<{ version: Publicprofile }, 'versionsPublicprofiles'>
        return enrichPublicProfileSeeMore(data.versionsPublicprofiles.docs[0].version)

    }
    else {
        type ProfileWithSnapshot = Publicprofile & {
            frozenBuildSnapshot?: {
                pageData?: Publicprofile
                metadataData?: Publicprofile
            } | null
        }

        const data: IGraphQLResponse<ProfileWithSnapshot, 'Publicprofiles'> =
            (await getCollectionSpecificFields(
                'Publicprofiles',
                `${individualDataFields}
                 freezeThisProfile
                 frozenBuildSnapshot`,
                `slug: { equals: "${individualSlug}"} `,
                1,
                isDraft,
            )) as IGraphQLResponse<ProfileWithSnapshot, 'Publicprofiles'>
        const profile = data.Publicprofiles.docs[0]

        if (profile?.freezeThisProfile && !profile.frozenBuildSnapshot?.pageData) {
            throw new Error(`Frozen profile "${individualSlug}" has no page snapshot. Republish it before building.`)
        }

        if (!profile) return null

        return enrichPublicProfileSeeMore(profile.freezeThisProfile
            ? profile.frozenBuildSnapshot!.pageData!
            : profile)
    }
}
